// src/components/canvass/AddUnitModal.tsx
// Modal for quickly adding a new unit at an existing address during canvassing
import React from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { WorkloadAddress } from '../../types/address.types';

export interface NewUnitData {
  unit: string;
  name: string;
}

export interface AddUnitModalProps {
  selectedAddress: WorkloadAddress;
  newUnit: NewUnitData;
  onUnitChange: (updates: Partial<NewUnitData>) => void;
  onSubmit: () => void;
  onClose: () => void;
  isSubmitting: boolean;
}

const AddUnitModal: React.FC<AddUnitModalProps> = ({
  selectedAddress,
  newUnit,
  onUnitChange,
  onSubmit,
  onClose,
  isSubmitting,
}) => {
  const handleClose = () => {
    onUnitChange({ unit: '', name: '' });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 md:items-center">
      <div className="bg-white w-full max-w-sm rounded-t-2xl md:rounded-2xl shadow-xl">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Add Unit</h2>
            <p className="text-sm text-gray-500">{selectedAddress.street}</p>
          </div>
          <button onClick={handleClose} className="p-2">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., 101, A, 2B"
              value={newUnit.unit}
              onChange={(e) => onUnitChange({ unit: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resident Name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="Name if known"
              value={newUnit.name}
              onChange={(e) => onUnitChange({ name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="p-4 border-t flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={!newUnit.unit.trim() || isSubmitting}
            className="flex-1 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="h-5 w-5 mr-2" />
                Add & Update
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddUnitModal;
